// /compare/shootproof: Pixally vs ShootProof.
//
// Read the header of honeybook.js first: every rule there applies here. ShootProof's
// facts, with sources and exact words: docs/geo/compare-gallery-platforms-evidence.md.
// Prints: Pixally sells prints and albums from the gallery, shipped to the US and
// Canada only (Ryan, 2 Oct 2026: on from launch; the /preview gallery section
// says the same). Which plans include it is not settled, so no page names a plan
// for it. The competitor's store stays on the page as its own edge.

export const ROUTE = '/compare/shootproof';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'ShootProof facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs ShootProof';
export const SEO_TITLE = 'Pixally vs ShootProof for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and ShootProof compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every ShootProof fact sourced.';

export const LEDE =
  'ShootProof is a client gallery platform that includes contracts, invoices, selling, slideshows and video in every paid plan, with online booking as an add-on. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with photo, film and hybrid galleries and print sales built in.';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you want galleries, print sales and simple contracts and invoices for little money, ShootProof covers that today. If you deliver a lot of film or run a crew, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The ShootProof column comes from ShootProof's own pricing pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and ShootProof compared',
    head: ['', 'Pixally', 'ShootProof'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects (no gallery storage)', 'Yes: 3 GB, photos only'],
      ['Plans, billed yearly', 'Basic $33, Pro $58, Studio $99 a month', 'Starter $8.33, Standard $16.67, Professional $26.67, Premium $50 a month'],
      ['Photo storage', '200 GB, 1 TB, unlimited', '20 GB or 100 GB shared with video, 1,000 GB, unlimited'],
      ['Video', '50 GB to start on paid plans, then packs from $50 for 50 GB; no limit on film length', 'Shared storage on Starter and Standard; 50 GB on Professional; 150 GB on Premium'],
      ['Contracts and invoices', 'Every plan', 'Every paid plan'],
      ['Booking and scheduling', 'Calendly scheduling on Basic and up', 'Add-on, $6.99 a month'],
      ['Print and product store', 'Yes: prints and albums, shipped to the US and Canada', 'Yes, commission-free'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', 'Not described on ShootProof\'s pricing page'],
      ['Post-production tracking', 'On Pro and Studio', 'Not described on ShootProof\'s pricing page'],
    ],
  },
  { type: 'heading', level: 2, text: 'When ShootProof is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'Commission-free print and product sales. Pixally sells prints and albums too, shipped only to the US and Canada.',
      'It costs far less: from $8.33 a month billed yearly, with contracts and invoices included.',
      'Every paid plan has every feature, and the plans differ only in storage, so you never upgrade for a tool.',
      'Its Premium plan pairs unlimited photos with 150 GB of video for $50 a month billed yearly.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you deliver a lot of film, run second shooters and editors, or want your bookings, contracts, payments, edit and delivery in one system.',
  },
  { type: 'heading', level: 3, text: 'Film delivery without a ceiling' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: 'ShootProof\'s video shares storage with photos on its lower plans and is capped at 50 GB or 150 GB on the higher ones. Pixally puts no limit on film length, plays films with chapters so a couple can jump to the ceremony or the first dance, and sells delivery in packs (50 GB for $50, 100 GB for $95, 500 GB for $450) that never expire.',
  },
  { type: 'heading', level: 3, text: 'The CRM and the delivery in one place' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: 'ShootProof covers contracts and invoices, with booking as an add-on. In Pixally the same project carries the inquiry, proposal, contract, payments, the edit through post-production (Pro and Studio), and the delivered gallery.',
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: "Second shooters get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).",
  },
  { type: 'heading', level: 2, text: 'Switching to Pixally' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Does ShootProof have a CRM?',
    a: 'It includes contracts and invoices in every paid plan, and online booking with automated emails and questionnaires as a $6.99 a month add-on.',
  },
  {
    q: 'How much video does ShootProof include?',
    a: 'Video shares storage with photos on Starter (20 GB) and Standard (100 GB); Professional includes 50 GB of video and Premium 150 GB.',
  },
  {
    q: 'Can I sell prints with Pixally?',
    a: 'Yes. Couples can order prints and albums from their gallery, shipped to the United States and Canada.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'ShootProof plans', url: 'https://www.shootproof.com/plans/' },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
